import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, FileText, TrendingUp, CheckCircle2, BarChart3, Target, ArrowRight, Download, Upload, AlertTriangle, Clock, Shield, Cpu, HardDrive, Activity } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AREAS = [
  { key: 'Personale', color: '#3BA99C' },
  { key: 'Terze Parti', color: '#60D1B4' },
  { key: 'Misure Organizzative', color: '#89E3FC' },
  { key: 'Sicurezza Fisica', color: '#5941F5' },
  { key: 'ICT', color: '#21358E' },
  { key: 'Incidenti di Sicurezza', color: '#E57D68' }
];

const Dashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [questionnaires, setQuestionnaires] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // CAA Ranking state (admin only)
  const [caaRanking, setCaaRanking] = useState(null);
  
  // Resource monitoring state (admin only)
  const [resources, setResources] = useState(null);
  
  // Backup & Restore state (admin only)
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupLoading, setBackupLoading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restorePreview, setRestorePreview] = useState(null);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);

  useEffect(() => {
    fetchData();
    if (user?.role === 'admin' || user?.role === 'super_admin') {
      fetchBackupStatus();
      fetchCaaRanking();
      fetchResources();
      // Aggiorna risorse ogni 30 secondi (solo se token valido)
      const interval = setInterval(() => {
        if (token) {
          fetchResources();
        }
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [token]);

  const fetchResources = async () => {
    if (!token) return; // Skip if no token
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/stats/resources`, { headers });
      setResources(response.data);
    } catch (error) {
      // Don't log 401 errors (expected when session expires)
      if (error.response?.status !== 401) {
        console.error('Error fetching resources:', error);
      }
    }
  };

  const fetchCaaRanking = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/analytics/caa-ranking`, { headers });
      setCaaRanking(response.data);
    } catch (error) {
      console.error('Error fetching CAA ranking:', error);
    }
  };

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/questionnaires`, { headers });
      setQuestionnaires(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchBackupStatus = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/backup/status`, { headers });
      setBackupStatus(response.data);
      
      // Mostra notifica se backup più vecchio di 7 giorni
      if (response.data?.last_backup_date) {
        const lastBackup = new Date(response.data.last_backup_date);
        const daysSince = Math.floor((Date.now() - lastBackup.getTime()) / (1000 * 60 * 60 * 24));
        if (daysSince >= 7) {
          toast.warning(`Backup scaduto da ${daysSince} giorni`, {
            description: 'Si consiglia di effettuare un nuovo backup per proteggere i dati.',
            duration: 8000,
            action: {
              label: 'Vai al Backup',
              onClick: () => {
                document.getElementById('backup-section')?.scrollIntoView({ behavior: 'smooth' });
              }
            }
          });
        }
      } else {
        // Nessun backup mai effettuato
        toast.warning('Nessun backup effettuato', {
          description: 'Si consiglia di effettuare il primo backup per proteggere i dati.',
          duration: 8000
        });
      }
    } catch (error) {
      console.error('Error fetching backup status:', error);
    }
  };
  
  const handleDownloadBackup = async () => {
    setBackupLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/backup/download`, { 
        headers,
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `backup_e-caa_${new Date().toISOString().split('T')[0]}.zip`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Backup scaricato con successo');
      fetchBackupStatus(); // Refresh status
    } catch (error) {
      console.error('Error downloading backup:', error);
      toast.error('Errore nel download del backup');
    } finally {
      setBackupLoading(false);
    }
  };
  
  const handleRestoreFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.zip')) {
      toast.error('Seleziona un file ZIP valido');
      return;
    }
    
    setRestoreFile(file);
    setRestoreLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/backup/restore/preview`, formData, { headers });
      setRestorePreview(response.data);
    } catch (error) {
      console.error('Error previewing restore:', error);
      toast.error(error.response?.data?.detail || 'Errore nella lettura del file');
      setRestoreFile(null);
      setRestorePreview(null);
    } finally {
      setRestoreLoading(false);
    }
  };
  
  const handleRestore = async (mode) => {
    if (!restoreFile) return;
    
    setRestoreLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const formData = new FormData();
      formData.append('file', restoreFile);
      
      const response = await axios.post(`${API}/backup/restore?mode=${mode}`, formData, { headers });
      
      toast.success(`Ripristino completato! ${JSON.stringify(response.data.restored_counts)}`);
      setRestoreFile(null);
      setRestorePreview(null);
      setShowRestoreConfirm(false);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error restoring:', error);
      toast.error(error.response?.data?.detail || 'Errore nel ripristino');
    } finally {
      setRestoreLoading(false);
    }
  };
  
  const getBackupStatusColor = () => {
    if (!backupStatus?.last_backup_date) return 'red';
    const lastBackup = new Date(backupStatus.last_backup_date);
    const daysSince = (Date.now() - lastBackup.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSince <= 7) return 'green';
    return 'red';
  };
  
  const getBackupStatusMessage = () => {
    if (!backupStatus?.last_backup_date) {
      return 'Nessun backup effettuato';
    }
    const lastBackup = new Date(backupStatus.last_backup_date);
    const daysSince = Math.floor((Date.now() - lastBackup.getTime()) / (1000 * 60 * 60 * 24));
    
    if (daysSince === 0) return 'Backup effettuato oggi';
    if (daysSince === 1) return 'Backup effettuato ieri';
    if (daysSince <= 7) return `Backup effettuato ${daysSince} giorni fa`;
    return `⚠️ Backup effettuato ${daysSince} giorni fa - Consigliato effettuare un nuovo backup`;
  };

  const createNewQuestionnaire = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.post(
        `${API}/questionnaires`,
        { title: `Valutazione ${new Date().toLocaleDateString('it-IT')}` },
        { headers }
      );
      toast.success('Questionario creato');
      navigate(`/questionario/${response.data.questionnaire_id}`);
    } catch (error) {
      console.error('Error creating questionnaire:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione del questionario');
    }
  };

  const completedQuestionnaires = questionnaires.filter(q => q.status === 'completed');
  const draftQuestionnaires = questionnaires.filter(q => q.status === 'draft');
  const latestCompleted = completedQuestionnaires[0];

  // Prepare chart data - sort by date (oldest first for correct trend display)
  const trendData = completedQuestionnaires
    .slice()
    .sort((a, b) => new Date(a.completed_at) - new Date(b.completed_at))
    .map(q => ({
      date: new Date(q.completed_at).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' }),
      score: q.total_score,
      title: q.title
    }));

  // Prepare radar data for latest questionnaire
  const radarData = latestCompleted?.area_scores 
    ? AREAS.map(area => ({
        area: area.key,
        score: latestCompleted.area_scores[area.key] || 0
      }))
    : [];

  // Average scores by area
  const avgAreaScores = AREAS.map(area => {
    const scores = completedQuestionnaires
      .filter(q => q.area_scores && q.area_scores[area.key] !== undefined)
      .map(q => q.area_scores[area.key]);
    const avg = scores.length > 0 ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
    return { area: area.key, score: Math.round(avg * 10) / 10, color: area.color };
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-label="Caricamento in corso">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3BA99C]" aria-hidden="true"></div>
        <span className="sr-only">Caricamento in corso...</span>
      </div>
    );
  }

  return (
    <main className="max-w-7xl mx-auto" role="main">
      {/* Header */}
      <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-slate-900" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="text-slate-500 mt-2">
            Benvenuto, {user?.full_name}
          </p>
        </div>
        {user?.role !== 'admin' && user?.role !== 'super_admin' && (
          <button
            onClick={createNewQuestionnaire}
            className="bg-[#3BA99C] hover:bg-[#2D8A7F] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3BA99C]"
            data-testid="new-questionnaire-btn"
            aria-label="Crea un nuovo questionario di valutazione"
          >
            <Plus className="w-5 h-5" aria-hidden="true" />
            Nuovo Questionario
          </button>
        )}
      </header>

      {/* Stats Cards */}
      <section aria-label="Statistiche questionari" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10">
        <Card className="bg-gradient-to-br from-[#3BA99C]/10 to-[#60D1B4]/10 border-[#3BA99C]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#3BA99C] flex items-center justify-center" aria-hidden="true">
                <FileText className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{questionnaires.length}</p>
                <p className="text-sm text-slate-500">Questionari Totali</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100/50 border-green-200/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-green-500 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{completedQuestionnaires.length}</p>
                <p className="text-sm text-slate-500">Completati</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50 border-amber-200/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">{draftQuestionnaires.length}</p>
                <p className="text-sm text-slate-500">In Bozza</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-[#5941F5]/10 to-[#21358E]/10 border-[#5941F5]/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-[#5941F5] flex items-center justify-center">
                <Target className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">
                  {latestCompleted?.total_score || '-'}%
                </p>
                <p className="text-sm text-slate-500">Ultimo Punteggio</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Charts Row */}
      {completedQuestionnaires.length > 0 ? (
        <section aria-label="Grafici di andamento" className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-10">
          {/* Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[#3BA99C]" />
                Andamento Punteggi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full overflow-x-auto">
                <LineChart width={500} height={260} data={trendData} margin={{ top: 10, right: 30, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#64748B" 
                    fontSize={12}
                  />
                  <YAxis 
                    stroke="#64748B" 
                    domain={[0, 100]} 
                    fontSize={12}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <Tooltip 
                    formatter={(value) => [`${value}%`, 'Punteggio']} 
                    labelFormatter={(label) => `Data: ${label}`}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="score" 
                    stroke="#3BA99C" 
                    strokeWidth={3}
                    dot={{ fill: '#3BA99C', r: 5 }}
                    name="Punteggio"
                  />
                </LineChart>
                {/* Legenda sotto il grafico */}
                <div className="flex justify-center mt-2">
                  <div className="flex items-center gap-2 text-sm text-slate-600">
                    <div className="w-4 h-1 bg-[#3BA99C] rounded"></div>
                    <span>Punteggio</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Grafico a Barre - Ultima Valutazione per Area */}
          {latestCompleted && radarData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#5941F5]" />
                  Ultima Valutazione per Area
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-w-md mx-auto">
                  {AREAS.map((area, idx) => {
                    const score = latestCompleted.area_scores?.[area.key] || 0;
                    return (
                      <div key={area.key} className="flex items-center gap-3">
                        <div className="w-28 text-sm text-slate-600 truncate" title={area.key}>
                          {area.key === 'Incidenti di Sicurezza' ? 'Incidenti' : 
                           area.key === 'Misure Organizzative' ? 'Misure Org.' :
                           area.key === 'Sicurezza Fisica' ? 'Sicurezza Fis.' : area.key}
                        </div>
                        <div className="flex-1 h-6 bg-slate-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full rounded-full transition-all duration-500 flex items-center justify-end pr-2"
                            style={{ 
                              width: `${Math.max(score, 5)}%`, 
                              backgroundColor: area.color 
                            }}
                          >
                            <span className="text-xs font-semibold text-white drop-shadow">{score}%</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </section>
      ) : (
        <Card className="bg-slate-50 mt-10">
          <CardContent className="py-12 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" aria-hidden="true" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">Nessun questionario completato</h3>
            <p className="text-slate-500 mb-6">
              {(user?.role === 'admin' || user?.role === 'super_admin')
                ? 'Gli utenti non hanno ancora completato questionari'
                : 'Completa il tuo primo questionario per vedere i grafici'}
            </p>
            {user?.role !== 'admin' && user?.role !== 'super_admin' && (
              <button
                onClick={createNewQuestionnaire}
                className="inline-flex items-center gap-2 bg-[#3BA99C] hover:bg-[#2D8A7F] text-white px-6 py-3 rounded-lg font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3BA99C]"
                aria-label="Inizia a compilare un nuovo questionario"
              >
                <Plus className="w-5 h-5" aria-hidden="true" />
                Inizia Ora
              </button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Average Scores by Area */}
      {completedQuestionnaires.length > 0 && (
        <Card className="mt-10">
          <CardHeader>
            <CardTitle>Punteggio Medio per Area</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {avgAreaScores.map((area) => (
                <div 
                  key={area.area}
                  className="p-4 rounded-xl border border-slate-100 hover:shadow-md transition-shadow"
                >
                  <div 
                    className="w-full h-2 rounded-full mb-3"
                    style={{ backgroundColor: `${area.color}20` }}
                  >
                    <div 
                      className="h-full rounded-full transition-all duration-500"
                      style={{ width: `${area.score}%`, backgroundColor: area.color }}
                    />
                  </div>
                  <p className="text-2xl font-bold" style={{ color: area.color }}>{area.score}%</p>
                  <p className="text-xs text-slate-500 mt-1">{area.area}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions - Moved above Backup & Restore */}
      {/* CAA Ranking - Admin Only */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && caaRanking && caaRanking.total_caas > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
          {/* Top 5 CAA */}
          <Card className="border-green-200 bg-gradient-to-br from-green-50/50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <TrendingUp className="w-5 h-5" />
                Top 5 CAA - Punteggi Migliori
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {caaRanking.top_5.map((caa, idx) => (
                  <div key={caa.caa_name} className="flex items-center justify-between py-2 border-b border-green-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-slate-700">{caa.caa_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-green-600">{caa.avg_score}%</span>
                      <span className="text-xs text-slate-400">({caa.count} quest.)</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Bottom 5 CAA */}
          <Card className="border-amber-200 bg-gradient-to-br from-amber-50/50 to-white">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <AlertTriangle className="w-5 h-5" />
                Bottom 5 CAA - Punteggi più Bassi
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {caaRanking.bottom_5.map((caa, idx) => (
                  <div key={caa.caa_name} className="flex items-center justify-between py-2 border-b border-amber-100 last:border-0">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">
                        {idx + 1}
                      </span>
                      <span className="font-medium text-slate-700">{caa.caa_name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-lg font-bold text-amber-600">{caa.avg_score}%</span>
                      <span className="text-xs text-slate-400">({caa.count} quest.)</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <nav aria-label="Azioni rapide" className="flex flex-wrap gap-4 mt-10">
        <button
          onClick={() => navigate('/questionari')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:border-[#3BA99C] hover:text-[#3BA99C] transition-colors focus:outline-none focus:ring-2 focus:ring-[#3BA99C]"
          aria-label="Vai alla pagina di tutti i questionari"
        >
          <FileText className="w-4 h-4" aria-hidden="true" />
          Vedi tutti i questionari
          <ArrowRight className="w-4 h-4" aria-hidden="true" />
        </button>
        {(user?.role === 'admin' || user?.role === 'super_admin') && (
          <button
            onClick={() => navigate('/statistiche')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg hover:border-[#5941F5] hover:text-[#5941F5] transition-colors focus:outline-none focus:ring-2 focus:ring-[#5941F5]"
            aria-label="Vai alla pagina delle statistiche avanzate"
          >
            <BarChart3 className="w-4 h-4" aria-hidden="true" />
            Statistiche Avanzate
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
      </nav>

      {/* Resource Monitoring - Admin Only */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && resources && (
        <Card className="border border-slate-200 mt-6">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Activity className="w-5 h-5 text-[#3BA99C]" />
              Monitoraggio Risorse Server
              <span className="text-xs font-normal text-slate-400 ml-auto">
                Aggiornamento ogni 30s
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              {/* CPU */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <Cpu className="w-4 h-4" />
                    CPU
                  </span>
                  <span className="text-sm text-slate-500">
                    {resources.cpu?.limit_vcpu} vCPU
                  </span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      resources.cpu?.usage_of_limit > 80 ? 'bg-red-500' :
                      resources.cpu?.usage_of_limit > 50 ? 'bg-yellow-500' : 'bg-[#3BA99C]'
                    }`}
                    style={{ width: `${Math.min(resources.cpu?.usage_of_limit || 0, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{resources.cpu?.current_percent || 0}% utilizzo</span>
                  <span className={`font-medium ${
                    resources.cpu?.usage_of_limit > 80 ? 'text-red-600' :
                    resources.cpu?.usage_of_limit > 50 ? 'text-yellow-600' : 'text-[#3BA99C]'
                  }`}>
                    {resources.cpu?.usage_of_limit || 0}% del limite
                  </span>
                </div>
              </div>

              {/* RAM */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-sm font-medium text-slate-700">
                    <HardDrive className="w-4 h-4" />
                    RAM
                  </span>
                  <span className="text-sm text-slate-500">
                    {resources.ram?.limit_mb} MB
                  </span>
                </div>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full transition-all duration-500 ${
                      resources.ram?.usage_of_limit > 80 ? 'bg-red-500' :
                      resources.ram?.usage_of_limit > 50 ? 'bg-yellow-500' : 'bg-[#3BA99C]'
                    }`}
                    style={{ width: `${Math.min(resources.ram?.usage_of_limit || 0, 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-slate-500">
                  <span>{resources.ram?.used_mb || 0} MB usati</span>
                  <span className={`font-medium ${
                    resources.ram?.usage_of_limit > 80 ? 'text-red-600' :
                    resources.ram?.usage_of_limit > 50 ? 'text-yellow-600' : 'text-[#3BA99C]'
                  }`}>
                    {resources.ram?.usage_of_limit || 0}% del limite
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backup & Restore Section - Admin Only (Last element) */}
      {(user?.role === 'admin' || user?.role === 'super_admin') && (
        <Card id="backup-section" className="border-2 border-dashed border-slate-200 mt-10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#21358E]" />
              Backup & Restore
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-8">
              {/* Backup Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  Scarica Backup
                </h3>
                
                {/* Backup Status */}
                <div className={`p-3 rounded-lg flex items-start gap-3 ${
                  getBackupStatusColor() === 'green' 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <Clock className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                    getBackupStatusColor() === 'green' ? 'text-green-600' : 'text-red-600'
                  }`} />
                  <div>
                    <p className={`text-sm font-medium ${
                      getBackupStatusColor() === 'green' ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {getBackupStatusMessage()}
                    </p>
                    {backupStatus?.last_backup_date && (
                      <p className="text-xs text-slate-500 mt-1">
                        {new Date(backupStatus.last_backup_date).toLocaleString('it-IT')}
                        {backupStatus.last_backup_by && ` • ${backupStatus.last_backup_by}`}
                      </p>
                    )}
                  </div>
                </div>
                
                <p className="text-sm text-slate-500">
                  Il backup include: utenti, CAA, uffici, questionari (con risposte e punteggi), documenti legali.
                </p>
                
                <button
                  onClick={handleDownloadBackup}
                  disabled={backupLoading}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#21358E] hover:bg-[#1a2a70] text-white rounded-lg transition-colors disabled:opacity-50"
                  data-testid="download-backup-btn"
                >
                  <Download className="w-4 h-4" />
                  {backupLoading ? 'Generazione in corso...' : 'Scarica Backup Completo'}
                </button>
              </div>
              
              {/* Restore Section */}
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                  <Upload className="w-4 h-4" />
                  Ripristina da Backup
                </h3>
                
                {!restorePreview ? (
                  <>
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center hover:border-[#3BA99C] transition-colors">
                      <input
                        type="file"
                        accept=".zip"
                        onChange={handleRestoreFileSelect}
                        className="hidden"
                        id="restore-file-input"
                        disabled={restoreLoading}
                      />
                      <label 
                        htmlFor="restore-file-input"
                        className="cursor-pointer"
                      >
                        <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                        <p className="text-sm text-slate-600">
                          {restoreLoading ? 'Lettura file...' : 'Clicca per selezionare il file .zip'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          oppure trascina qui
                        </p>
                      </label>
                    </div>
                    <p className="text-sm text-slate-500">
                      Carica un file di backup precedentemente scaricato per ripristinare i dati.
                    </p>
                  </>
                ) : (
                  <div className="space-y-3">
                    {/* Preview */}
                    <div className="bg-slate-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <span className="font-medium text-slate-800">File: {restoreFile?.name}</span>
                        <button
                          onClick={() => { setRestoreFile(null); setRestorePreview(null); }}
                          className="text-xs text-slate-500 hover:text-red-500"
                        >
                          Annulla
                        </button>
                      </div>
                      
                      {restorePreview.checksums_valid ? (
                        <p className="text-xs text-green-600 mb-2">✓ Integrità file verificata</p>
                      ) : (
                        <p className="text-xs text-orange-600 mb-2">⚠️ Impossibile verificare integrità</p>
                      )}
                      
                      <p className="text-xs text-slate-500 mb-2">
                        Backup del: {new Date(restorePreview.manifest?.backup_date).toLocaleString('it-IT')}
                      </p>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-white p-2 rounded">
                          <span className="text-slate-500">Utenti:</span>
                          <span className="ml-1 font-medium">{restorePreview.manifest?.counts?.users || 0}</span>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-slate-500">CAA:</span>
                          <span className="ml-1 font-medium">{restorePreview.manifest?.counts?.caas || 0}</span>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-slate-500">Questionari:</span>
                          <span className="ml-1 font-medium">{restorePreview.manifest?.counts?.questionnaires || 0}</span>
                        </div>
                        <div className="bg-white p-2 rounded">
                          <span className="text-slate-500">Doc. legali:</span>
                          <span className="ml-1 font-medium">{restorePreview.manifest?.counts?.legal_documents || 0}</span>
                        </div>
                      </div>
                    </div>
                    
                    {!showRestoreConfirm ? (
                      <button
                        onClick={() => setShowRestoreConfirm(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-[#3BA99C] hover:bg-[#2D8A7F] text-white rounded-lg transition-colors"
                      >
                        <Upload className="w-4 h-4" />
                        Procedi con il Ripristino
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                            <div className="text-sm">
                              <p className="font-medium text-amber-800">Scegli modalità di ripristino:</p>
                              <p className="text-amber-700 text-xs mt-1">
                                <strong>Merge:</strong> Aggiunge solo dati mancanti (più sicuro)<br/>
                                <strong>Sostituisci:</strong> Sovrascrive tutti i dati esistenti
                              </p>
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRestore('merge')}
                            disabled={restoreLoading}
                            className="flex-1 px-4 py-2 bg-[#3BA99C] hover:bg-[#2D8A7F] text-white rounded-lg text-sm disabled:opacity-50"
                          >
                            {restoreLoading ? 'Ripristino...' : 'Merge'}
                          </button>
                          <button
                            onClick={() => handleRestore('replace')}
                            disabled={restoreLoading}
                            className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg text-sm disabled:opacity-50"
                          >
                            {restoreLoading ? 'Ripristino...' : 'Sostituisci'}
                          </button>
                        </div>
                        <button
                          onClick={() => setShowRestoreConfirm(false)}
                          className="w-full text-xs text-slate-500 hover:text-slate-700"
                        >
                          Annulla
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
};

export default Dashboard;
